// client/src/pages/Profile.js
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import ProModal from '../components/ProModal';

const s = {
  page: { maxWidth:820, margin:'0 auto', padding:'40px 24px' },
  pageTitle: { fontFamily:'var(--font-display)', fontSize:32, marginBottom:4, color:'var(--ink)' },
  pageSub: { color:'var(--muted)', fontSize:14, marginBottom:36 },
  tabs: { display:'flex', gap:0, borderBottom:'1px solid var(--line)', marginBottom:32 },
  tab: { padding:'10px 20px', fontSize:14, fontWeight:500, cursor:'pointer', color:'var(--muted)', borderBottom:'2px solid transparent' },
  tabActive: { color:'var(--ink)', borderBottom:'2px solid var(--ink)' },
  section: { marginBottom:32 },
  sectionTitle: { fontSize:11, fontWeight:700, letterSpacing:'2px', textTransform:'uppercase', color:'var(--muted-2)', fontFamily:'var(--font-mono)', marginBottom:16, paddingBottom:8, borderBottom:'1px solid var(--line)' },
  card: { background:'var(--card)', border:'1px solid var(--line-card)', borderRadius:0, padding:'24px' },
  subCard: { border:'1px solid var(--line-card)', borderRadius:0, padding:'20px 24px', marginBottom:12, display:'flex', alignItems:'center', justifyContent:'space-between', gap:16 },
  label: { fontSize:12, color:'var(--muted-2)', fontFamily:'var(--font-mono)', letterSpacing:'1px', textTransform:'uppercase', marginBottom:4 },
  value: { fontSize:15, color:'var(--ink)', fontWeight:500 },
  group: { marginBottom:16 },
  inputLabel: { display:'block', fontSize:12, fontWeight:600, letterSpacing:'1.5px', marginBottom:6, textTransform:'uppercase', color:'var(--muted-2)', fontFamily:'var(--font-mono)' },
  input: { width:'100%', padding:'10px 14px', border:'1px solid var(--line)', borderRadius:0, fontSize:14, outline:'none', fontFamily:'var(--font-body)' },
  btn: { background:'var(--ink)', color:'var(--paper)', border:'none', padding:'10px 22px', fontSize:14, fontWeight:600, borderRadius:0, cursor:'pointer' },
  outlineBtn: { background:'transparent', color:'var(--ink)', border:'1.5px solid var(--ink)', padding:'10px 22px', fontSize:14, fontWeight:500, borderRadius:0, cursor:'pointer' },
  dangerBtn: { background:'transparent', color:'var(--danger)', border:'1.5px solid var(--danger)', padding:'10px 22px', fontSize:14, fontWeight:500, borderRadius:0, cursor:'pointer' },
  proCard: { background:'var(--ink)', color:'var(--paper)', borderRadius:0, padding:'28px 24px', marginBottom:20 },
  proTitle: { fontFamily:'var(--font-display)', fontSize:22, marginBottom:6 },
  proBadge: { display:'inline-block', border:'1px solid var(--muted)', color:'var(--muted-2)', fontSize:10, padding:'2px 8px', letterSpacing:'2px', fontFamily:'var(--font-mono)', marginBottom:12, textTransform:'uppercase' },
  planRow: { display:'flex', alignItems:'baseline', gap:8, marginBottom:4 },
  planPrice: { fontFamily:'var(--font-mono)', fontSize:32, fontWeight:700 },
  planPer: { color:'var(--muted-2)', fontSize:14 },
  period: { fontSize:13, color:'var(--muted-2)', marginTop:4 },
  upgradeBtn: { background:'var(--paper)', color:'var(--ink)', border:'none', padding:'12px 28px', fontSize:14, fontWeight:700, borderRadius:0, cursor:'pointer', marginTop:16 },
  freeCard: { border:'1px solid var(--line-card)', background:'var(--card)', borderRadius:0, padding:'24px' },
  freeTier: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 },
  featureList: { listStyle:'none', paddingLeft:0 },
  featureItem: { fontSize:13, color:'var(--muted)', padding:'6px 0', borderBottom:'1px solid var(--line-card)', display:'flex', gap:8 },
  statusBadge: { display:'inline-flex', alignItems:'center', gap:4, padding:'3px 10px', borderRadius:0, fontSize:12, fontWeight:600 },
  success: { background:'#f0fdf4', color:'#15803d', padding:'12px', borderRadius:0, fontSize:13, marginBottom:16 },
  error: { background:'#fef2f2', color:'#b91c1c', padding:'12px', borderRadius:0, fontSize:13, marginBottom:16 }
};

export default function Profile() {
  const { user, refreshUser, isPro } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('subscription');
  const [showPro, setShowPro] = useState(searchParams.get('upgrade') === 'true');
  const [profileForm, setProfileForm] = useState({ name: user?.name || '' });
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState('');
  const [savedStudies, setSavedStudies] = useState([]);
  const [savedSearches, setSavedSearches] = useState(user?.savedSearches || []);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const canceled = searchParams.get('canceled');

    if (sessionId) {
      // Verify the Stripe session and activate Pro
      (async () => {
        try {
          setMsg({ type: 'success', text: 'Verifying your payment...' });
          await axios.get('/api/stripe/verify-session', { params: { session_id: sessionId } });
          await refreshUser();
          setMsg({ type: 'success', text: 'Subscription activated! Welcome to Pro. 🎉' });
        } catch (err) {
          setMsg({ type: 'error', text: 'Could not verify payment. Please contact support.' });
        }
      })();
    } else if (searchParams.get('success')) {
      refreshUser();
      setMsg({ type: 'success', text: 'Subscription activated! Welcome to Pro.' });
    }

    if (canceled) {
      setMsg({ type: 'error', text: 'Checkout canceled. No charge was made.' });
    }
  }, []);

  useEffect(() => {
    if (user?.savedStudies?.length) setSavedStudies(user.savedStudies);
    if (user?.savedSearches?.length) setSavedSearches(user.savedSearches);
  }, [user]);

  const handleProfileSave = async () => {
    setLoading('profile');
    try {
      await axios.put('/api/auth/me', { name: profileForm.name });
      await refreshUser();
      setMsg({ type:'success', text:'Profile updated.' });
    } catch { setMsg({ type:'error', text:'Failed to update profile.' }); }
    setLoading('');
  };

  const handlePasswordChange = async () => {
    if (pwForm.newPassword !== pwForm.confirm) return setMsg({ type:'error', text:'Passwords do not match.' });
    setLoading('password');
    try {
      await axios.put('/api/auth/change-password', { currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
      setPwForm({ currentPassword:'', newPassword:'', confirm:'' });
      setMsg({ type:'success', text:'Password changed.' });
    } catch (err) { setMsg({ type:'error', text: err.response?.data?.message || 'Failed.' }); }
    setLoading('');
  };

  const handlePortal = async () => {
    setLoading('portal');
    try {
      const { data } = await axios.post('/api/stripe/portal');
      window.location.href = data.url;
    } catch { setMsg({ type:'error', text:'Failed to open billing portal.' }); }
    setLoading('');
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel subscription? You keep Pro access until the end of your billing period.')) return;
    setLoading('cancel');
    try {
      const { data } = await axios.post('/api/stripe/cancel');
      await refreshUser();
      setMsg({ type:'success', text:'Subscription will cancel at end of billing period.' });
    } catch { setMsg({ type:'error', text:'Failed to cancel.' }); }
    setLoading('');
  };

  const handleReactivate = async () => {
    setLoading('reactivate');
    try {
      await axios.post('/api/stripe/reactivate');
      await refreshUser();
      setMsg({ type:'success', text:'Subscription reactivated!' });
    } catch { setMsg({ type:'error', text:'Failed to reactivate.' }); }
    setLoading('');
  };

  const sub = user?.subscription;
  const proActive = isPro();

  const tabs = ['subscription', 'account', 'saved', 'searches', 'security'];

  return (
    <div style={s.page} className="fade-in">
      {showPro && <ProModal onClose={() => setShowPro(false)} />}
      <h1 style={s.pageTitle}>Account</h1>
      <p style={s.pageSub}>{user?.name} · {user?.email}</p>

      {msg.text && <div style={msg.type === 'success' ? s.success : s.error}>{msg.text} <button style={{ float:'right', background:'none', border:'none', cursor:'pointer', color:'inherit' }} onClick={() => setMsg({ type:'', text:'' })}>×</button></div>}

      <div className="profile-tabs" style={s.tabs}>
        {tabs.map(t => <div key={t} style={{ ...s.tab, ...(activeTab === t ? s.tabActive : {}) }} onClick={() => setActiveTab(t)}>{t.charAt(0).toUpperCase() + t.slice(1)}</div>)}
      </div>

      {activeTab === 'subscription' && (
        <div>
          {proActive ? (
            <div style={s.proCard}>
              <div style={s.proBadge}>Pro Plan</div>
              <div style={s.proTitle}>You're on Pro</div>
              <div style={s.planRow}>
                <span style={s.planPrice}>{sub?.plan === 'annual' ? '$99' : '$9'}</span>
                <span style={s.planPer}>/ {sub?.plan === 'annual' ? 'year' : 'month'}</span>
              </div>
              {sub?.currentPeriodEnd && <div style={s.period}>{sub?.cancelAtPeriodEnd ? `Cancels on ${new Date(sub.currentPeriodEnd).toLocaleDateString()}` : `Renews on ${new Date(sub.currentPeriodEnd).toLocaleDateString()}`}</div>}
              <div className="pro-buttons" style={{ display:'flex', gap:10, marginTop:20 }}>
                <button style={s.upgradeBtn} onClick={handlePortal} disabled={loading === 'portal'}>
                  {loading === 'portal' ? 'Loading...' : 'Manage Billing ↗'}
                </button>
                {sub?.cancelAtPeriodEnd ? (
                  <button style={{ ...s.upgradeBtn, background:'transparent', color:'var(--paper)', border:'1.5px solid var(--paper)' }} onClick={handleReactivate} disabled={loading === 'reactivate'}>
                    {loading === 'reactivate' ? 'Loading...' : 'Reactivate'}
                  </button>
                ) : (
                  <button style={{ ...s.upgradeBtn, background:'transparent', color:'var(--danger)', border:'1.5px solid var(--danger)' }} onClick={handleCancel} disabled={loading === 'cancel'}>
                    {loading === 'cancel' ? 'Canceling...' : 'Cancel Plan'}
                  </button>
                )}
                {sub?.plan === 'monthly' && !sub?.cancelAtPeriodEnd && (
                  <button style={{ ...s.upgradeBtn, background:'transparent', color:'var(--paper)', border:'1.5px solid var(--paper)' }} onClick={() => setShowPro(true)}>
                    Switch to Annual (Save 50%)
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div style={s.freeCard}>
              <div style={s.freeTier}>
                <div>
                  <div style={{ fontWeight:700, fontSize:16, marginBottom:4, color:'var(--ink)' }}>Free Plan</div>
                  <div style={{ color:'var(--muted)', fontSize:13 }}>Basic access to clinical trials</div>
                </div>
                <button style={{ ...s.btn, fontSize:14 }} onClick={() => setShowPro(true)}>Upgrade to Pro →</button>
              </div>
              <ul style={s.featureList}>
                {['Search all 500,000+ studies','View full study details','Save up to 5 studies','—  Advanced filters (Pro)','—  Bulk export (Pro)','—  Save search presets (Pro)','—  Priority API access (Pro)'].map((f, i) => (
                  <li key={i} style={{ ...s.featureItem, color: f.startsWith('—') ? 'var(--muted-2)' : 'var(--muted)' }}><span>{f.startsWith('—') ? '🔒' : '✓'}</span>{f.replace('— ','')}</li>
                ))}
              </ul>
              <div className="free-plan-cta" style={{ marginTop:20, padding:'16px', background:'var(--ink)', borderRadius:0, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div>
                  <div style={{ color:'var(--paper)', fontWeight:600, fontSize:15 }}>Pro from $9/month</div>
                  <div style={{ color:'var(--muted-2)', fontSize:12, marginTop:2 }}>or $99/year · 52% off regular price</div>
                </div>
                <button style={{ background:'var(--paper)', color:'var(--ink)', border:'none', padding:'10px 20px', fontWeight:700, fontSize:14, borderRadius:0, cursor:'pointer' }} onClick={() => setShowPro(true)}>Get Pro</button>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'account' && (
        <div>
          <div style={s.section}>
            <div style={s.sectionTitle}>Profile Information</div>
            <div style={s.card}>
              <div style={s.group}>
                <label style={s.inputLabel}>Full Name</label>
                <input style={s.input} type="text" value={profileForm.name} onChange={e => setProfileForm({...profileForm, name: e.target.value})} onFocus={e => e.target.style.borderColor='var(--ink)'} onBlur={e => e.target.style.borderColor='var(--line)'} />
              </div>
              <div style={s.group}>
                <label style={s.inputLabel}>Email</label>
                <input style={{ ...s.input, background:'var(--paper-2)', color:'var(--muted-2)' }} type="email" value={user?.email} disabled />
              </div>
              <div style={{ display:'flex', gap:8, marginTop:4 }}>
                <button style={s.btn} onClick={handleProfileSave} disabled={loading === 'profile'}>{loading === 'profile' ? 'Saving...' : 'Save Changes'}</button>
              </div>
            </div>
          </div>
          <div style={s.section}>
            <div style={s.sectionTitle}>Account Details</div>
            <div className="profile-details-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              {[
                { label:'Member Since', value: user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—' },
                { label:'Role', value: user?.role?.replace('_', ' ').toUpperCase() || 'USER' },
                { label:'Subscription', value: sub?.plan?.toUpperCase() || 'FREE' },
                { label:'Status', value: proActive ? 'Active Pro' : 'Free Tier' }
              ].map(item => (
                <div key={item.label} style={{ background:'var(--paper-2)', border:'1px solid var(--line)', padding:'14px 16px', borderRadius:0 }}>
                  <div style={s.label}>{item.label}</div>
                  <div style={s.value}>{item.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'saved' && (
        <div>
          <div style={s.sectionTitle}>Saved Studies ({savedStudies.length})</div>
          {savedStudies.length === 0 ? (
            <div style={{ textAlign:'center', padding:'40px', color:'var(--muted)', border:'1px dashed var(--line)', borderRadius:0 }}>
              <div style={{ fontSize:32, marginBottom:8 }}>☆</div>
              <div>No saved studies yet. Browse and save studies from the search page.</div>
            </div>
          ) : (
            <div>
              {savedStudies.map(nctId => (
                <div key={nctId} style={{ ...s.subCard, cursor:'pointer' }} onClick={() => window.location.href = `/study/${nctId}`}>
                  <div>
                    <div style={{ fontFamily:'var(--font-mono)', fontSize:13, fontWeight:600, color:'var(--ink)' }}>{nctId}</div>
                    <div style={{ fontSize:12, color:'var(--muted)' }}>Click to view study →</div>
                  </div>
                </div>
              ))}
              {!proActive && savedStudies.length >= 5 && (
                <div style={{ padding:'12px 16px', background:'var(--paper-2)', border:'1px solid var(--line)', borderRadius:0, marginTop:12, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <span style={{ fontSize:13, color:'var(--muted)' }}>Free limit: 5 saved studies</span>
                  <button style={s.btn} onClick={() => setShowPro(true)}>Upgrade for unlimited</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'security' && (
        <div style={s.section}>
          <div style={s.sectionTitle}>Change Password</div>
          <div style={s.card}>
            <div style={s.group}>
              <label style={s.inputLabel}>Current Password</label>
              <input style={s.input} type="password" value={pwForm.currentPassword} onChange={e => setPwForm({...pwForm, currentPassword: e.target.value})} placeholder="••••••••" onFocus={e => e.target.style.borderColor='var(--ink)'} onBlur={e => e.target.style.borderColor='var(--line)'} />
            </div>
            <div style={s.group}>
              <label style={s.inputLabel}>New Password</label>
              <input style={s.input} type="password" value={pwForm.newPassword} onChange={e => setPwForm({...pwForm, newPassword: e.target.value})} placeholder="Min. 6 characters" onFocus={e => e.target.style.borderColor='var(--ink)'} onBlur={e => e.target.style.borderColor='var(--line)'} />
            </div>
            <div style={s.group}>
              <label style={s.inputLabel}>Confirm New Password</label>
              <input style={s.input} type="password" value={pwForm.confirm} onChange={e => setPwForm({...pwForm, confirm: e.target.value})} placeholder="Repeat new password" onFocus={e => e.target.style.borderColor='var(--ink)'} onBlur={e => e.target.style.borderColor='var(--line)'} />
            </div>
            <button style={s.btn} onClick={handlePasswordChange} disabled={loading === 'password'}>
              {loading === 'password' ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}